import { format } from "date-fns";
import { Activity, RefreshCw, Users } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FreshnessBadge } from "@/features/dashboard/shell";

import { MEDDREAM_STALE_MS } from "../constants";
import { MEDDREAM_LABELS } from "../labels";
import type { IMeddreamHourlyMetric } from "../schemas/meddream-hourly";

export interface CurrentHourHeaderProps {
  current: IMeddreamHourlyMetric | null | undefined;
  isLoading?: boolean;
}

/**
 * Estado operativo aproximado de la hora UTC en curso.
 */
export function CurrentHourHeader({
  current,
  isLoading,
}: CurrentHourHeaderProps): React.JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-lg">Estado MedDream</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Hora UTC en curso como proxy de carga actual (cron cada 5 min)
          </p>
        </div>
        <FreshnessBadge updatedAt={current?.updatedAt} staleMs={MEDDREAM_STALE_MS} />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800"
              />
            ))}
          </div>
        ) : !current ? (
          <p className="text-sm text-muted-foreground">
            Aún no hay documento para la hora UTC actual. El cron puede estar
            pendiente o el backfill incompleto.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat
              icon={Activity}
              label={MEDDREAM_LABELS.currentLoad.label}
              title={MEDDREAM_LABELS.currentLoad.description}
              value={`${current.activeConnectionsAvg} / ${current.activeConnectionsMax}`}
            />
            <Stat
              icon={Users}
              label={MEDDREAM_LABELS.sessionsInHour.label}
              title={MEDDREAM_LABELS.sessionsInHour.description}
              value={String(current.uniqueSessionsCount)}
            />
            <Stat
              icon={RefreshCw}
              label={MEDDREAM_LABELS.restarts.label}
              title={MEDDREAM_LABELS.restarts.description}
              value={String(current.restartsCount)}
              accent={current.restartsCount > 0}
            />
          </div>
        )}
        {current?.updatedAt && (
          <p
            className="mt-3 text-xs text-muted-foreground"
            title={MEDDREAM_LABELS.updatedAt.description}
          >
            {MEDDREAM_LABELS.updatedAt.label}:{" "}
            {format(current.updatedAt, "yyyy-MM-dd HH:mm:ss")} (local) · {current.id} UTC
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  icon: Icon,
  label,
  title,
  value,
  accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  title?: string;
  value: string;
  accent?: boolean;
}): React.JSX.Element {
  return (
    <div
      className="rounded-lg border border-border-light p-3 dark:border-border-dark"
      title={title}
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          accent ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-gray-100"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
